import { Dialog } from '@base-ui/react/dialog';
import { XIcon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import Button from './Button';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  cta?: React.ReactNode;
  hideCloseButton?: boolean;
  disableBackdropClick?: boolean;
  className?: string;
  /** When true, body content does not scroll — the child must manage overflow. */
  overflowHidden?: boolean;
  /** Optional classes for the header row containing the title and close button. */
  headerClassName?: string;
  /** Controls rendered to the left of the header close button. */
  headerActions?: React.ReactNode;
  /** Size of the header close button. */
  closeButtonSize?: 'sm' | 'md';
  /**
   * Base UI dialog modality. `trap-focus` keeps focus in the dialog without
   * locking document scroll (avoids the page scrollbar appearing/disappearing).
   */
  modal?: boolean | 'trap-focus';
  'aria-label'?: string;
}

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  cta,
  hideCloseButton = false,
  disableBackdropClick = false,
  className = '',
  overflowHidden = false,
  headerClassName = '',
  headerActions,
  closeButtonSize = 'sm',
  modal = true,
  'aria-label': ariaLabel
}) => {
  const dialogLabel = ariaLabel ?? (typeof title === 'string' ? title : undefined);
  const titleRender = typeof title === 'string' ? undefined : <div />;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      disablePointerDismissal={disableBackdropClick}
      modal={modal}
    >
      <Dialog.Portal>
        {/* Base UI drops a nested dialog's backdrop, which leaves a modal opened from a side panel unveiled. */}
        <Dialog.Backdrop forceRender className="fixed inset-0 z-[1100] bg-black bg-opacity-40" />
        <Dialog.Viewport className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
          <Dialog.Popup
            aria-label={dialogLabel}
            className={twMerge(
              'bg-white p-5 rounded-lg shadow-lg relative flex flex-col gap-2 max-h-[80vh] max-w-[90ch] outline-none',
              className
            )}
          >
            <div className={twMerge('flex justify-between items-center flex-shrink-0 font-medium', headerClassName)}>
              {title != null && title !== false ? (
                <Dialog.Title className="min-w-0 flex-1" render={titleRender}>
                  {title}
                </Dialog.Title>
              ) : (
                dialogLabel == null && <Dialog.Title className="sr-only">Dialog</Dialog.Title>
              )}
              <div className="flex items-center gap-2 shrink-0">
                {headerActions}
                <Dialog.Close
                  className={hideCloseButton ? 'sr-only' : undefined}
                  render={
                    hideCloseButton ? (
                      <button type="button" aria-label="Close modal" />
                    ) : (
                      <Button variant="outline" size={closeButtonSize} icon={XIcon} aria-label="Close modal" />
                    )
                  }
                />
              </div>
            </div>
            <div
              className={twMerge(
                'flex-1 flex flex-col min-h-0',
                overflowHidden ? 'overflow-hidden' : 'overflow-y-auto'
              )}
            >
              {children}
            </div>
            {cta && (
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-border-subtle flex-shrink-0">
                {cta}
              </div>
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
